<script>
   /*************************************************************/
   /* AppContainer for prediction                               */
   /* -------------------------                                 */
   /* app container for applying PCA model                      */
   /*************************************************************/

   import { Container, Button } from 'svelte-controls-basic';
   import { untrack } from 'svelte';

   import { replacer } from '../../../shared/utils.js';
   import DataLoader from '../../../shared/components/PCADataLoader.svelte';
   import ResultTable from '../shared/ModelTable.svelte';
   import Outliers from '../../../shared/widgets/Outliers.svelte';
   import Contributions from '../../../shared/components/Contributions.svelte';

   import { downloadResults } from '../utils';

   import ModelLoader from './ModelLoader.svelte';
   import AppPlots from './AppPlots.svelte';

   // WASM promise and session object from parent app
   let {WASM, backupSession, session = $bindable()} = $props();

   // get part of the session for this tab
   const s = $derived(session['new']);

   // properties for showing contributions
   let selected = $state(-1);
   let ncomp = $state(1);
   let contributionIsShown = $state(false);

   // callback function to set object with a give position in plot series
   // as outlier
   function markOutlier(pos) {
      // check that position is valid
      if (pos < 0 || pos > s.resobj.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.resobj.objindices[pos];

      // add the object to outliers array and reset the model
      if (id >= 0 && !s.outliers.includes(id)) {
         s.outliers.push(id);
         s.resobj = null;
      }
   }

   // show modal dialog with contributions for selected object
   function showContributions(pos, ncomp) {
      if (pos < 0) return;
      selected = pos;
      ncomp = ncomp;
      contributionIsShown = true;
   }

   // hide modal dialog with contributions
   function hideContributions() {
      contributionIsShown = false;
      selected = -1;
      ncomp = 1;
   }

   // callback to reset outliers
   function resetOutliers() {
      s.outliers = [];
      s.resobj = null;
   }

   // callback to reset outliers
   function undoOutliers() {
      s.outliers.pop();
      s.resobj = null;
   }


   // effect to track changes in dataset and models
   $effect(() => {
      if (s.model === null) {
         // if training set is set null - reset model and all related parameters
         s.dataset = null;
      }

      // if model object is set to null - reset dependent objects
      if (s.dataset === null) {
         s.outliers = [];
         s.resobj = null;
      }
   });

   $effect(() => {
      const sessionStr = untrack(() => JSON.stringify(session, replacer));
      backupSession(sessionStr, s.model, s.dataset, s.resobj);
   })

</script>

{#if contributionIsShown}
<Contributions
   {...s}
   {ncomp}
   selected={s.resobj.objindices[selected]}
   closeModal={hideContributions}
/>
{/if}

<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="model-loader">
         <ModelLoader bind:model={s.model} />
      </div>

      {#if s.model}
      <div class="data-loader">
         <DataLoader
            {WASM}
            bind:dataset={s.dataset}
            bind:resobj={s.resobj}
            outliers={s.outliers}
            model={s.model}
            className="Group"
         />
      </div>
      {/if}

      {#if s.outliers && s.outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers dataset={s.dataset} outliers={s.outliers} {resetOutliers} {undoOutliers} />
      </div>
      {/if}


      {#if s.resobj}
      <div class="model-dowloader">
         <Container label='Download'>
            <div class="buttons">
            <Button text="Results" onclick={() => downloadResults(s.resobj, s.dataset, "new", s.outliers)} />
            </div>
         </Container>
      </div>
      <div class="result-stats">
         <ResultTable resobj={s.resobj} />
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !s.resobj}
         <div class="help-text">
            <p>
               In this tab you can project new data points to a previosly trained PCA model and explore main outcomes: scores, distances and variances.
            </p>
         {#if !s.model}
            <p><strong>Step 1.</strong></p>
            <p>Load file with PCA model you created earlier in tab <strong>Train</strong></p>
         {:else if !s.dataset}
            <p><strong>Step 2.</strong></p>
            <p>Load your dataset set from a CSV file. The interface and selection steps are similar to the ones you used in <em>Train</em> tab. However, in this case you do not need to specify all details for header, just select if the new dataset has header as a first row or not. All values will be inherited from the model object.
            </p>
            <p><strong>It is important that the new dataset has same variables as the training set used to create the model.</strong></p>
         {/if}
         <p></p>
         </div>
      {:else}
         {#key s.resobj }
         <AppPlots {markOutlier} {showContributions} {...s} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0 1em 0 0.5em;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      border-radius: 0.25rem;
   }

   .app-controls > .result-stats {
      background: #e8e8e8;
   }

   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }


   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }

   .result-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }


   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(320px, 30%) 1fr;
      }
   }

</style>